import React from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { AddressType } from 'components/AddressSearch/addressTypes';
import type { FormDataType } from 'contexts/FormProvider';
import { AUSTRALIA } from 'utils/constants';
import useAddressChangeHandler from './useAddressChangeHandler';
import useAddressDefaults from './useAddressDefaults';

interface UseContactAddressHandlersParams {
  form: UseFormReturn<FormDataType>;
  qasMigrationEnabled: boolean;
}

/**
 * Encapsulates all address-related logic for ContactDetails forms:
 * change handlers, defaults getters, and legacy state registration.
 * Components receive the return values and decide when to use them.
 */
export const useContactAddressHandlers = ({ form, qasMigrationEnabled }: UseContactAddressHandlersParams) => {
  const { getValues, register, watch } = form;

  const isManualAddressRes = getValues('isManualAddressRes') as boolean;
  const isManualAddressPhy = getValues('isManualAddressPhy') as boolean;
  const isResidentialCountryAU = watch('rCountry') === AUSTRALIA;
  const isPhysicalCountryAU = watch('pCountry') === AUSTRALIA;

  // Legacy: register rState/pState for the old AddressManual component when it is active.
  // Can be removed once DcomPrtrQasMigrationToggle is fully rolled out.
  React.useEffect(() => {
    if (qasMigrationEnabled) return;
    if (isManualAddressRes && isResidentialCountryAU) register('rState');
    if (isManualAddressPhy && isPhysicalCountryAU) register('pState');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManualAddressRes, isResidentialCountryAU, isManualAddressPhy, isPhysicalCountryAU]);

  const handleRepPostalAddressChange = useAddressChangeHandler(AddressType.RepPostal, form);
  const handlePhysicalAddressChange = useAddressChangeHandler(AddressType.Physical, form);

  const getRepPostalAddressDefaults = useAddressDefaults(AddressType.RepPostal, form);
  const getPhysicalAddressDefaults = useAddressDefaults(AddressType.Physical, form);

  return {
    handleRepPostalAddressChange,
    handlePhysicalAddressChange,
    getRepPostalAddressDefaults,
    getPhysicalAddressDefaults,
    isManualAddressRes,
    isManualAddressPhy,
  };
};
